import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/vendor-master`;
const PAGE_SIZE = 50;
const CURRENCIES = ['USD','EUR','GBP','SAR','AED','BRL','CHF','JPY','CNY','INR','CAD','AUD','SGD','HKD','KWD','QAR','BHD','OMR','EGP','JOD','TRY','ZAR','MXN','KRW','SEK','NOK','DKK','PLN','CZK','HUF'];

interface Vendor {
  id: number; vendor_id: string; vendor_name: string; lifecycle_status: string;
  payment_terms: string; currency: string; vendor_type: string;
  bank_name: string; bank_account_number: string; bank_routing_number: string;
  iban: string; swift_code: string; beneficiary_name: string;
  bank_country: string; bank_currency: string; address_country: string;
  tax_id: string; version: number; created_at: string; approved_at: string;
  source_system: string;
}
interface Summary {
  total: number; active: number; pending: number; pending_approval: number; blocked: number;
}

const emptyForm = {
  vendor_id: '', vendor_name: '', payment_terms: 'NET30', currency: 'USD', vendor_type: '',
  bank_name: '', bank_account_number: '', bank_routing_number: '',
  iban: '', swift_code: '', beneficiary_name: '', bank_country: '', bank_currency: '',
  entity_code: '', tax_id: '', address_country: '',
};

export default function VendorManagement() {
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBank, setEditBank] = useState({ bank_name: '', bank_account_number: '', bank_routing_number: '', iban: '', swift_code: '', beneficiary_name: '', bank_country: '', bank_currency: '' });

  const startEdit = (v: Vendor) => {
    setEditingId(v.id);
    setEditBank({
      bank_name: v.bank_name || '', bank_account_number: v.bank_account_number || '',
      bank_routing_number: v.bank_routing_number || '', iban: v.iban || '',
      swift_code: v.swift_code || '', beneficiary_name: v.beneficiary_name || '',
      bank_country: v.bank_country || '', bank_currency: v.bank_currency || '',
    });
  };

  const saveBank = async (v: Vendor) => {
    try {
      const res = await authFetch(`${API}/${v.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editBank, expected_version: v.version }),
      });
      if (res.ok) { showToast('Bank details saved', true); setEditingId(null); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Save failed', false); }
    } catch { showToast('Save failed', false); }
  };

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    if (search) p.set('search', search);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setVendors(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, search, entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { showToast('Vendor created', true); setShowCreate(false); setForm({ ...emptyForm }); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed to create vendor', false); }
  };

  const handleAction = async (id: number, action: string, version: number) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version }) });
      if (res.ok) { showToast(`Vendor ${action.replace('-', ' ')}`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Action failed', false); }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };
  const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };
  const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap', padding: '10px 14px', textAlign: 'left' };
  const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };
  const labelStyle: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 };

  return (
    <PageLayout title="Vendor Management" subtitle="Create, approve, and manage vendor master data" action={{ label: '+ New Vendor', onClick: () => setShowCreate(true) }}>
      {summary && (
        <KpiGrid items={[
          { label: 'Total', value: summary.total, color: 'var(--text-primary)' },
          { label: 'Active', value: summary.active, color: 'var(--positive)' },
          { label: 'Pending', value: summary.pending, color: 'var(--warning)' },
          { label: 'Pending Approval', value: summary.pending_approval, color: 'var(--accent)' },
          { label: 'Blocked', value: summary.blocked, color: 'var(--negative)' },
        ]} />
      )}

      <FilterBar filters={[
        { key: 'search', label: 'vendors', type: 'search', value: search, onChange: v => { setSearch(v); setPage(0); }, width: 260, placeholder: 'Search vendors...' },
        { key: 'status', label: 'Statuses', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, options: ['PENDING', 'PENDING_APPROVAL', 'ACTIVE', 'INACTIVE', 'BLOCKED'].map(s => ({ value: s, label: s })) },
      ]} />

      {/* Vendor Table with expandable detail */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {['Vendor ID', 'Name', 'Type', 'Terms', 'Currency', 'Country', 'Status', 'Actions'].map(h => (
              <th key={h} style={thStyle}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {vendors.map(v => (
              <>
                <tr key={v.id} onClick={() => setExpandedId(expandedId === v.id ? null : v.id)} style={{ cursor: 'pointer', background: expandedId === v.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{v.vendor_id}</td>
                  <td style={tdStyle}>{v.vendor_name}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{v.vendor_type || '-'}</td>
                  <td style={tdStyle}>{v.payment_terms}</td>
                  <td style={tdStyle}>{v.currency}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{v.address_country || '-'}</td>
                  <td style={tdStyle}><StatusBadge status={v.lifecycle_status} /></td>
                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {v.lifecycle_status === 'PENDING' && <ActionButton label="Submit" onClick={() => handleAction(v.id, 'submit-approval', v.version)} variant="secondary" size="sm" />}
                      {v.lifecycle_status === 'PENDING_APPROVAL' && <ActionButton label="Approve" onClick={() => handleAction(v.id, 'approve', v.version)} variant="success" size="sm" />}
                      {v.lifecycle_status === 'ACTIVE' && <ActionButton label="Block" onClick={() => handleAction(v.id, 'block', v.version)} variant="danger" size="sm" />}
                      {(v.lifecycle_status === 'INACTIVE' || v.lifecycle_status === 'BLOCKED') && <ActionButton label="Reactivate" onClick={() => handleAction(v.id, 'reactivate', v.version)} variant="success" size="sm" />}
                    </div>
                  </td>
                </tr>
                {expandedId === v.id && (
                  <tr key={`${v.id}-detail`}><td colSpan={8} style={{ padding: '0 14px 16px 32px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 12 }}>
                      {/* General */}
                      <div>
                        <div style={labelStyle}>Tax ID</div>
                        <div style={{ fontSize: 13 }}>{v.tax_id || '-'}</div>
                      </div>
                      <div>
                        <div style={labelStyle}>Source</div>
                        <div style={{ fontSize: 13 }}>{v.source_system || '-'}</div>
                      </div>
                      <div>
                        <div style={labelStyle}>Created</div>
                        <div style={{ fontSize: 13 }}>{v.created_at ? new Date(v.created_at).toLocaleDateString() : '-'}</div>
                      </div>
                      <div>
                        <div style={labelStyle}>Approved</div>
                        <div style={{ fontSize: 13 }}>{v.approved_at ? new Date(v.approved_at).toLocaleDateString() : '-'}</div>
                      </div>
                    </div>

                    {/* Bank Details Section — view or edit mode */}
                    <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 6, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bank Details</div>
                        {editingId === v.id
                          ? <div style={{ display: 'flex', gap: 6 }}>
                              <ActionButton label="Save" onClick={() => saveBank(v)} variant="success" size="sm" />
                              <ActionButton label="Cancel" onClick={() => setEditingId(null)} variant="secondary" size="sm" />
                            </div>
                          : (v.lifecycle_status === 'PENDING' || v.lifecycle_status === 'ACTIVE') && <ActionButton label="Edit Bank Details" onClick={() => startEdit(v)} variant="secondary" size="sm" />
                        }
                      </div>
                      {editingId === v.id ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                          <div><div style={labelStyle}>Bank Name</div><input value={editBank.bank_name} onChange={e => setEditBank({ ...editBank, bank_name: e.target.value })} style={inputStyle} placeholder="Bank Name" /></div>
                          <div><div style={labelStyle}>Beneficiary Name</div><input value={editBank.beneficiary_name} onChange={e => setEditBank({ ...editBank, beneficiary_name: e.target.value })} style={inputStyle} placeholder="Beneficiary Name" /></div>
                          <div><div style={labelStyle}>Account Number</div><input value={editBank.bank_account_number} onChange={e => setEditBank({ ...editBank, bank_account_number: e.target.value })} style={inputStyle} placeholder="Account Number" /></div>
                          <div><div style={labelStyle}>Routing Number</div><input value={editBank.bank_routing_number} onChange={e => setEditBank({ ...editBank, bank_routing_number: e.target.value })} style={inputStyle} placeholder="Routing / Sort Code" /></div>
                          <div><div style={labelStyle}>IBAN</div><input value={editBank.iban} onChange={e => setEditBank({ ...editBank, iban: e.target.value })} style={inputStyle} placeholder="IBAN" /></div>
                          <div><div style={labelStyle}>SWIFT / BIC</div><input value={editBank.swift_code} onChange={e => setEditBank({ ...editBank, swift_code: e.target.value })} style={inputStyle} placeholder="SWIFT / BIC" /></div>
                          <div><div style={labelStyle}>Bank Country</div><input value={editBank.bank_country} onChange={e => setEditBank({ ...editBank, bank_country: e.target.value })} style={inputStyle} placeholder="e.g. US, GB, SA" /></div>
                          <div><div style={labelStyle}>Bank Currency</div>
                            <select title="Bank Currency" value={editBank.bank_currency} onChange={e => setEditBank({ ...editBank, bank_currency: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                              <option value="">Select</option>
                              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                            <div><div style={labelStyle}>Bank Name</div><div style={{ fontSize: 13, fontWeight: 600 }}>{v.bank_name || '-'}</div></div>
                            <div><div style={labelStyle}>Beneficiary Name</div><div style={{ fontSize: 13 }}>{v.beneficiary_name || v.vendor_name || '-'}</div></div>
                            <div><div style={labelStyle}>Account Number</div><div style={{ fontSize: 13, fontFamily: 'monospace' }}>{v.bank_account_number || '-'}</div></div>
                            <div><div style={labelStyle}>Routing Number</div><div style={{ fontSize: 13, fontFamily: 'monospace' }}>{v.bank_routing_number || '-'}</div></div>
                            <div><div style={labelStyle}>IBAN</div><div style={{ fontSize: 13, fontFamily: 'monospace' }}>{v.iban || '-'}</div></div>
                            <div><div style={labelStyle}>SWIFT / BIC</div><div style={{ fontSize: 13, fontFamily: 'monospace' }}>{v.swift_code || '-'}</div></div>
                            <div><div style={labelStyle}>Bank Country</div><div style={{ fontSize: 13 }}>{v.bank_country || '-'}</div></div>
                            <div><div style={labelStyle}>Bank Currency</div><div style={{ fontSize: 13 }}>{v.bank_currency || v.currency || '-'}</div></div>
                          </div>
                          {!v.bank_name && !v.iban && !v.bank_account_number && (
                            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--warning)', fontStyle: 'italic' }}>No bank details on file — payment processing will require manual bank info.</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Vendor Statement Reconciliation */}
                    <VendorStatementSection vendorId={v.vendor_id} />
                  </td></tr>
                )}
              </>
            ))}
            {!loading && vendors.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>No vendors found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      {/* Create Vendor Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Vendor" size="lg" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select title="Legal Entity" value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={selectStyle}>
            <option value="">Select Legal Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} — {e.entity_name} ({e.functional_currency})</option>)}
          </select>

          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Vendor ID (e.g. VEND-001)" value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} style={inputStyle} />
            <input placeholder="Vendor Name *" value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} style={inputStyle} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <select title="Payment Terms" value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} style={selectStyle}>
              {['NET30', 'NET60', 'NET15', 'NET90', '2/10 NET30', 'IMMEDIATE', 'COD'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select title="Currency" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={selectStyle}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="Country (e.g. US, SA)" value={form.address_country} onChange={e => setForm({ ...form, address_country: e.target.value })} style={{ ...inputStyle, maxWidth: 100 }} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Vendor Type (e.g. SUPPLIER)" value={form.vendor_type} onChange={e => setForm({ ...form, vendor_type: e.target.value })} style={inputStyle} />
            <input placeholder="Tax ID" value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} style={inputStyle} />
          </div>

          {/* Bank Details Section */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bank Details</div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input placeholder="Bank Name" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} style={inputStyle} />
              <input placeholder="Beneficiary Name (if different from vendor)" value={form.beneficiary_name} onChange={e => setForm({ ...form, beneficiary_name: e.target.value })} style={inputStyle} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input placeholder="Account Number" value={form.bank_account_number} onChange={e => setForm({ ...form, bank_account_number: e.target.value })} style={inputStyle} />
              <input placeholder="Routing / Sort Code" value={form.bank_routing_number} onChange={e => setForm({ ...form, bank_routing_number: e.target.value })} style={inputStyle} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input placeholder="IBAN" value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} style={inputStyle} />
              <input placeholder="SWIFT / BIC Code" value={form.swift_code} onChange={e => setForm({ ...form, swift_code: e.target.value })} style={{ ...inputStyle, maxWidth: 150 }} />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <input placeholder="Bank Country (e.g. US, GB, SA)" value={form.bank_country} onChange={e => setForm({ ...form, bank_country: e.target.value })} style={{ ...inputStyle, maxWidth: 150 }} />
              <select title="Bank Currency" value={form.bank_currency} onChange={e => setForm({ ...form, bank_currency: e.target.value })} style={{ ...selectStyle, maxWidth: 150 }}>
                <option value="">Bank Currency</option>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}


function VendorStatementSection({ vendorId }: { vendorId: string }) {
  const [statements, setStatements] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [expandedStmt, setExpandedStmt] = useState<any>(null);
  const [msg, setMsg] = useState('');

  const loadStatements = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/${vendorId}/statements`);
      if (res.ok) {
        const d = await res.json();
        // Backend returns {items: [...], total: N}; tolerate older
        // shape that returned a bare array (defensive against either
        // shape so a server-side change can't crash the panel).
        setStatements(Array.isArray(d) ? d : (d.items || []));
      }
    } catch { /* ignore */ }
  }, [vendorId]);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  const handleUpload = async (file: File) => {
    setUploading(true); setMsg('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await authFetch(`${API}/${vendorId}/statements/upload`, { method: 'POST', body: fd });
      if (res.ok) { const d = await res.json(); setMsg(`Reconciled: ${d.matched_count || 0} matched, ${d.unmatched_count || 0} unmatched`); loadStatements(); }
      else { const e = await res.json(); setMsg(e.detail || 'Upload failed'); }
    } catch { setMsg('Upload failed'); }
    setUploading(false);
  };

  const viewStatement = async (id: number) => {
    if (expandedStmt?.id === id) { setExpandedStmt(null); return; }
    try {
      const res = await authFetch(`${API}/statements/${id}`);
      if (res.ok) setExpandedStmt(await res.json());
    } catch { /* ignore */ }
  };

  const cellStyle: React.CSSProperties = { padding: '6px 10px', fontSize: 12 };

  return (
    <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 6, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Statement Reconciliation</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {msg && <span style={{ fontSize: 11, color: msg.includes('fail') ? 'var(--negative)' : 'var(--positive)' }}>{msg}</span>}
          <label style={{ cursor: 'pointer', padding: '4px 10px', borderRadius: 4, background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 600 }}>
            {uploading ? 'Uploading...' : 'Upload CSV'}
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
          </label>
        </div>
      </div>

      {statements.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--text-hint)', padding: 8 }}>No statements uploaded. Upload a vendor statement CSV to auto-reconcile against AP invoices.</div>
      ) : (
        <div>
          {statements.map((s: any) => (
            <div key={s.id} style={{ marginBottom: 8 }}>
              <div onClick={() => viewStatement(s.id)} style={{ cursor: 'pointer', padding: '6px 10px', borderRadius: 4, background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                <span><strong>{s.filename || `Statement #${s.id}`}</strong> — {s.period || 'N/A'}</span>
                <span>Matched: {s.matched_count || 0} | Unmatched: {s.unmatched_count || 0} | {s.status || 'RECONCILED'}</span>
              </div>
              {expandedStmt?.id === s.id && expandedStmt.lines && (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4, fontSize: 11 }}>
                  <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Date', 'Doc #', 'Description', 'Amount', 'Match', 'AP Invoice'].map(h => (
                      <th key={h} style={{ ...cellStyle, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, fontSize: 10 }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>{expandedStmt.lines.map((l: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)', background: l.matched ? 'transparent' : '#fff7ed' }}>
                      <td style={cellStyle}>{l.doc_date || '-'}</td>
                      <td style={cellStyle}>{l.doc_number || '-'}</td>
                      <td style={cellStyle}>{(l.description || '').substring(0, 40)}</td>
                      <td style={{ ...cellStyle, fontWeight: 600 }}>{formatAmount(l.amount || 0)}</td>
                      <td style={cellStyle}>{l.matched ? <StatusBadge status="MATCHED" /> : <StatusBadge status="UNMATCHED" />}</td>
                      <td style={{ ...cellStyle, color: 'var(--accent)' }}>{l.matched_invoice || '-'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
